import { WidgetRuntimeError } from "./errors/widget-runtime-error.js";
import { InvalidAccessToken } from "./errors/invalid-access-token.js";

/**
 * @class
 * @classdesc Manages, loads, disposes a Davinci widget as well, provides useful methods to manage the widget cycle.
 * @version 1.0
 * @author Alberto Fernandez
 */
export class WidgetManager extends HTMLElement {
    static observedAttributes = ["companyId", "policyId"];

    static #PING_ROOT_URL = "https://auth.pingone.com/";

    #widgetContainer;
    widgetState = "NOT INITIALIZED";
    #lastToken = "";

    constructor() {
        super();
    }

    connectedCallback() {
        this.#widgetContainer = this;
    }

    /**
     * Retrieves the Davinci API.
     * 
     * @throws {ReferenceError} Davinci API could not be located
     * @returns {any} Davinci API reference
     */
    #retrieveApi() {
        if (!window.davinci) {
            throw new ReferenceError("API is not present in the window context");
        }
        return window.davinci;
    }

    /**
     * Renders a widget on the container.
     * 
     * @param {string} accessToken Generated access token. Do not reuse access tokens
     * @returns {WidgetManager}
     * @throws {WidgetRuntimeError} There was an error trying to initialize the widget
     * @throws {InvalidAccessToken} Provided token not valid or expired
     */
    renderWidget(accessToken) {
        this.widgetState = "START RUNNING";

        const companyId = this.attributes.getNamedItem("companyid")?.value;
        const policyId = this.attributes.getNamedItem("policyid")?.value;

        if (!companyId || !policyId) {
            throw new ReferenceError("Company ID and policy ID are required parameters");
        }

        this.disposeWidget();
        this.#validateAccessToken(accessToken);

        try {
            // Establish last token as the new one
            // this will allow to validate non-same token when re-rendering
            this.#lastToken = accessToken;

            const props = {
                config: {
                    method: "runFlow",
                    apiRoot: WidgetManager.#PING_ROOT_URL,
                    accessToken: accessToken,
                    companyId: companyId,
                    policyId: policyId,
                },
                useModal: false,
                successCallback: data => this.#successCallbackWrapper(data),
                errorCallback: error => this.#errorCallbackWrapper(error),
            }

            this.#retrieveApi().skRenderScreen(
                this.#widgetContainer,
                props
            );

            this.widgetState = "RUNNING";
        } catch (error) {
            this.disposeWidget();

            this.widgetState = "ERROR";
            throw new WidgetRuntimeError(error);
        }
    }

    /**
     * Validates that the token is not expired or either in an invalid format.
     * 
     * @param {string} token Generated access token
     */
    #validateAccessToken(token) {
        try {
            if (!token) {
                throw new Error("No token were provided");
            }

            if (token === this.#lastToken) {
                throw new Error("Access token reusage is not permitted. Please generate a new one");
            }

            // Split the token into its parts
            const parts = token.split('.');
            if (parts.length !== 3) {
                throw new Error("Invalid token parts");
            }

            // Decode the payload (second part)
            const base64Url = parts[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const decodedPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            // Parse the JSON payload
            const payload = JSON.parse(decodedPayload);

            // Get the expiration time (exp)
            const expirationTime = payload.exp;

            // Get the current time in seconds since epoch
            const currentTime = Math.floor(Date.now() / 1000);

            // Check if the token has expired
            if (currentTime > expirationTime) {
                throw new Error("Token is expired");
            }

        } catch (error) {
            throw new InvalidAccessToken(error.message, error);
        }
    }

    /**
     * Wraps success widget signals, so the widget can be disposed automatically, and then
     * invokes the actual successCallback.
     * 
     * @param {any} data
     * @callback WidgetManager#successCallback
     */
    #successCallbackWrapper(data) {
        this.disposeWidget();
        this.widgetState = "SUCCEDED";

        this.dispatchEvent(new CustomEvent("success", { detail: this.#retrieveCallbackResponse(data), }));
    }

    /**
     * Wraps error widget signals, so the widget can be disposed automatically, and then
     * return the proper error the the actual errorCallback.
     * 
     * @param {any} error 
     * @callback WidgetManager#errorCallback
     * @throws {WidgetSessionTimeoutError} Flow timed out
     */
    #errorCallbackWrapper(error) {
        this.disposeWidget();
        this.widgetState = "ERROR";

        const errorResponse = this.#retrieveCallbackResponse(error);

        if (errorResponse.httpResponseCode === 401 || errorResponse.errorMessage?.toLowerCase() === "unauthorized!") {
            this.dispatchEvent(new CustomEvent("session-timeout", { detail: error }));
            return;
        }

        if (errorResponse.additionalProperties) {
            const { errorCode, errorMessage } = errorResponse.additionalProperties;

            if (errorCode?.toLowerCase() === "400" || errorMessage?.toLowerCase() === "cancel") {
                this.dispatchEvent(new CustomEvent("user-cancelled", { detail: error, }));
                return;
            }
        }

        this.dispatchEvent(new CustomEvent("error", { detail: error, }));
    }

    /**
     * Retrieves the callback response.
     * 
     * @param {object} rawResponse Response from flow/widget
     * @returns {{response: string, signedResponse: string|null}} If response where signed, will be decoded and signedResponse field will have the original JWT.
     */
    #retrieveCallbackResponse(rawResponse) {
        if (rawResponse.signedResponse) {
            return JSON.parse(atob(rawResponse.signedResponse.split('.')[1]));
        }
        return rawResponse;
    }

    /**
     * Disposes the widget. Does not remove the specified widget container.
     * 
     * @returns {void}
     */
    disposeWidget() {
        try {
            this.#retrieveApi().cleanup(this.#widgetContainer);
        } catch (error) {
            console.warn("Unable to dispose widget due to", error);
        }
    }

    /**
     * Stringfies the widget manager class instance.
     * 
     * @override
     */
    toString() {
        return `WidgetManager. Current state: ${this.widgetState} - Containerized on: ${this.#widgetContainer.outerHTML}`;
    }
}

customElements.define("nw-davinci-widget", WidgetManager);